import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { cn } from './cn';
import { CHECKBOX } from './checkbox';

/**
 * The console's one data table.
 *
 * PHASE 07's brief said to build this "on the existing TanStack Table usage
 * rather than beside it". There is no existing TanStack Table usage —
 * `@tanstack/react-table` was a declared dependency with zero imports anywhere in
 * src/ (removed 2026-10-30), and every list in the console renders this component instead. The intent
 * of the instruction (do not end up with two table primitives) is honoured by
 * extending this one additively: every prop below is optional, so the eight
 * existing call sites compile and render exactly as before, and the students
 * screens in PHASES 08–12 opt into the new behaviour a prop at a time.
 *
 * What it now does beyond drawing rows:
 *  - a density scale, because a triage list and a reference list want different
 *    row heights and the answer is not "pick one for everybody";
 *  - a sticky header, so column identity survives a long scroll;
 *  - sortable columns, with the sort state owned by the caller (it belongs in the
 *    URL, next to the filters — see FilterBar);
 *  - column visibility, persisted by the caller for the same reason;
 *  - a real keyboard row model. A clickable `<tr>` with no tabIndex and no key
 *    handler is a row only a mouse can open, which is what this was.
 */

export type TableDensity = 'compact' | 'cosy' | 'comfortable';
export type SortDirection = 'asc' | 'desc';
export interface SortState {
  columnId: string;
  direction: SortDirection;
}

export interface Column<T> {
  /** Stable key. Also the id used in `sort` and `hiddenColumns`. */
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Right-align numeric/mono columns. */
  align?: 'start' | 'end';
  className?: string;
  /** Offer a sort control in this header. Requires `onSortChange`. */
  sortable?: boolean;
  /** Offer this column in the visibility menu. Defaults to true. */
  hideable?: boolean;
  /**
   * Plain-text header for the visibility menu and the sort announcement, needed
   * when `header` is a node rather than a string.
   */
  headerLabel?: string;
}

/** Row padding per density. The 44px minimum touch target holds at every step. */
const DENSITY: Record<TableDensity, { cell: string; head: string }> = {
  compact: { cell: 'px-md py-[6px]', head: 'px-md py-[6px]' },
  cosy: { cell: 'px-md py-sm', head: 'px-md py-sm' },
  comfortable: { cell: 'px-md py-[14px]', head: 'px-md py-sm' },
};

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onToggleSelectAll?: (checked: boolean) => void;
  /**
   * Accessible name for the table. Every table should have one; it is optional
   * only so the pre-existing call sites keep compiling, and it is rendered as a
   * visually-hidden `<caption>` rather than a `aria-label` so it survives
   * translation and shows up in the a11y tree with the right role.
   */
  caption?: string;
  density?: TableDensity;
  onDensityChange?: (density: TableDensity) => void;
  /** Header stays put while the body scrolls. Needs `maxHeight` to do anything. */
  stickyHeader?: boolean;
  /** e.g. `'70vh'`. Turns the wrapper into a scroll container. */
  maxHeight?: string;
  sort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;
  /** Column ids the user has switched off. */
  hiddenColumns?: string[];
  onHiddenColumnsChange?: (ids: string[]) => void;
  /** Rendered in place of the body when `rows` is empty — normally an `EmptyState`. */
  emptyState?: ReactNode;
  className?: string;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  selectable,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  caption,
  density = 'cosy',
  onDensityChange,
  stickyHeader,
  maxHeight,
  sort,
  onSortChange,
  hiddenColumns,
  onHiddenColumnsChange,
  emptyState,
  className,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const captionId = useId();
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  // Roving tabindex: exactly one row is in the tab order at a time, so Tab moves
  // past the table rather than through every row of a 100-row page.
  const [focusedIndex, setFocusedIndex] = useState(0);

  const hidden = useMemo(() => new Set(hiddenColumns ?? []), [hiddenColumns]);
  const visibleColumns = useMemo(() => columns.filter((c) => !hidden.has(c.id)), [columns, hidden]);
  const pad = DENSITY[density];

  const allSelected = Boolean(
    selectable && rows.length > 0 && rows.every((r) => selectedIds?.has(rowKey(r))),
  );
  const someSelected = Boolean(
    selectable && rows.some((r) => selectedIds?.has(rowKey(r))) && !allSelected,
  );

  const labelOf = (c: Column<T>) => c.headerLabel ?? (typeof c.header === 'string' ? c.header : c.id);

  const toggleSort = (columnId: string) => {
    if (!onSortChange) return;
    // asc → desc → off. The third state matters: without it a user cannot get
    // back to the server's default ordering once they have touched a column.
    if (sort?.columnId !== columnId) onSortChange({ columnId, direction: 'asc' });
    else if (sort.direction === 'asc') onSortChange({ columnId, direction: 'desc' });
    else onSortChange(null);
  };

  const focusRow = useCallback((index: number) => {
    const rowEls = bodyRef.current?.querySelectorAll<HTMLTableRowElement>('tr[data-row]');
    const el = rowEls?.[index];
    if (!el) return;
    setFocusedIndex(index);
    el.focus();
  }, []);

  const onRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, row: T, index: number) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusRow(Math.min(index + 1, rows.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusRow(Math.max(index - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        focusRow(0);
        break;
      case 'End':
        event.preventDefault();
        focusRow(rows.length - 1);
        break;
      case 'Enter':
        event.preventDefault();
        onRowClick?.(row);
        break;
      case ' ':
      case 'Spacebar':
        // Space selects when the table is selectable, opens when it is not —
        // matching how a file list behaves in every desktop OS.
        event.preventDefault();
        if (selectable) onToggleSelect?.(rowKey(row));
        else onRowClick?.(row);
        break;
      default:
        break;
    }
  };

  const [frameRef, scrollable] = useScrollableFrame();
  const interactive = Boolean(onRowClick);
  const showToolbar = Boolean(onDensityChange || onHiddenColumnsChange);

  return (
    <div className={cn('flex flex-col gap-xs', className)}>
      {showToolbar && (
        <TableToolbar
          density={density}
          onDensityChange={onDensityChange}
          columns={columns.filter((c) => c.hideable !== false).map((c) => ({ id: c.id, label: labelOf(c) }))}
          hidden={hidden}
          onHiddenColumnsChange={onHiddenColumnsChange}
        />
      )}

      <div
        ref={frameRef}
        /* A scroll container only a mouse can reach is a column a keyboard user
           cannot read — and this frame scrolls on every narrow viewport, at 200%
           zoom, and whenever a list has more columns than the window. So when it
           actually overflows it becomes a focusable, named region and the arrow
           keys scroll it (WCAG 2.1.1); when it fits it stays out of the tab
           order, because a tab stop that does nothing is its own defect.
           Chrome makes overflow containers focusable by itself. Firefox and
           Safari do not. */
        tabIndex={scrollable ? 0 : undefined}
        role={scrollable ? 'region' : undefined}
        aria-label={scrollable ? (caption ?? t('ui.scrollableTable')) : undefined}
        className={cn(
          'overflow-auto rounded-md border-[0.5px] border-hairline bg-canvas-paper',
          scrollable && 'focus-visible:shadow-focus focus-visible:outline-hidden',
          maxHeight && 'overscroll-contain',
        )}
        style={maxHeight ? { maxHeight } : undefined}
      >
        <table className="w-full border-collapse text-start" aria-describedby={caption ? captionId : undefined}>
          {caption && (
            <caption id={captionId} className="sr-only">
              {caption}
            </caption>
          )}
          <thead className={cn(stickyHeader && 'sticky top-0 z-10 bg-canvas-paper')}>
            <tr className="border-b-[0.5px] border-hairline">
              {selectable && (
                <th
                  scope="col"
                  className={cn('w-[44px] cursor-pointer', pad.head)}
                  // SC 2.5.8: the box is 18px, but the TARGET is the cell. A
                  // click that lands on the input is handled by the input; one
                  // that lands anywhere else in the cell is forwarded here.
                  onClick={(e) => {
                    if ((e.target as HTMLElement).tagName !== 'INPUT') {
                      onToggleSelectAll?.(!allSelected);
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('ui.selectAllRows')}
                    checked={allSelected}
                    ref={(el) => {
                      // The half-state is the honest one when a page is partly
                      // selected; without it the box reads "nothing is selected".
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={(e) => onToggleSelectAll?.(e.target.checked)}
                    className={CHECKBOX}
                  />
                </th>
              )}
              {visibleColumns.map((c) => {
                const active = sort?.columnId === c.id;
                const sortable = Boolean(c.sortable && onSortChange);
                return (
                  <th
                    key={c.id}
                    scope="col"
                    aria-sort={
                      !sortable ? undefined : active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'
                    }
                    className={cn(
                      pad.head,
                      'text-label uppercase tracking-widest text-ink-mute',
                      c.align === 'end' ? 'text-end' : 'text-start',
                    )}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(c.id)}
                        className={cn(
                          'inline-flex items-center gap-2xs rounded-xs text-label uppercase tracking-widest',
                          'transition-colors duration-almanac hover:text-accent-blue focus-visible:shadow-focus',
                          active ? 'text-accent-blue' : 'text-ink-mute',
                        )}
                        aria-label={t(
                          active && sort.direction === 'asc'
                            ? 'ui.sortDescendingBy'
                            : active
                              ? 'ui.sortClearBy'
                              : 'ui.sortAscendingBy',
                          { column: labelOf(c) },
                        )}
                      >
                        {c.header}
                        <SortGlyph direction={active ? sort.direction : undefined} />
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody ref={bodyRef}>
            {rows.length === 0 && emptyState ? (
              <tr>
                <td colSpan={visibleColumns.length + (selectable ? 1 : 0)} className="p-0">
                  {emptyState}
                </td>
              </tr>
            ) : (
              rows.map((row, index) => {
                const id = rowKey(row);
                const isSelected = selectedIds?.has(id) ?? false;
                return (
                  <tr
                    key={id}
                    data-row
                    // A row that opens a record is an interactive control and
                    // needs to be reachable, activatable and announced as one.
                    tabIndex={interactive || selectable ? (index === focusedIndex ? 0 : -1) : undefined}
                    onFocus={() => setFocusedIndex(index)}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    onKeyDown={
                      interactive || selectable ? (e) => onRowKeyDown(e, row, index) : undefined
                    }
                    aria-selected={selectable ? isSelected : undefined}
                    className={cn(
                      'border-b-[0.5px] border-hairline last:border-b-0 transition-colors duration-almanac',
                      interactive && 'cursor-pointer hover:bg-canvas-bone',
                      (interactive || selectable) &&
                        'focus-visible:outline-hidden focus-visible:shadow-focus focus-visible:relative focus-visible:z-1',
                      isSelected && 'bg-canvas-sand',
                    )}
                  >
                    {selectable && (
                      <td
                        className={cn(pad.cell, 'cursor-pointer')}
                        onClick={(e) => {
                          // Never opens the row: this cell selects. The input
                          // handles its own clicks; the rest of the cell is
                          // forwarded, so the target is 44px wide rather than
                          // the 13px the browser gives a bare checkbox.
                          e.stopPropagation();
                          if ((e.target as HTMLElement).tagName !== 'INPUT') {
                            onToggleSelect?.(id);
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          aria-label={t('ui.selectRow', { id })}
                          checked={isSelected}
                          onChange={() => onToggleSelect?.(id)}
                          className={CHECKBOX}
                        />
                      </td>
                    )}
                    {visibleColumns.map((c) => (
                      <td
                        key={c.id}
                        className={cn(
                          pad.cell,
                          'text-small text-ink-graphite',
                          c.align === 'end' ? 'text-end' : 'text-start',
                          c.className,
                        )}
                      >
                        {c.cell(row)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SortGlyph({ direction }: { direction?: SortDirection }) {
  return (
    <span aria-hidden className="text-label leading-none">
      {direction === 'asc' ? '↑' : direction === 'desc' ? '↓' : '↕'}
    </span>
  );
}

/** Density + column-visibility controls. Rendered only when the caller wires them. */
function TableToolbar({
  density,
  onDensityChange,
  columns,
  hidden,
  onHiddenColumnsChange,
}: {
  density: TableDensity;
  onDensityChange?: (d: TableDensity) => void;
  columns: { id: string; label: string }[];
  hidden: Set<string>;
  onHiddenColumnsChange?: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  const densities: TableDensity[] = ['compact', 'cosy', 'comfortable'];

  const toggleColumn = (id: string) => {
    if (!onHiddenColumnsChange) return;
    const next = new Set(hidden);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    // Never let the user hide the last column — an empty table is not a view.
    if (next.size >= columns.length) return;
    onHiddenColumnsChange([...next]);
  };

  return (
    <div className="flex flex-wrap items-center justify-end gap-xs">
      {onDensityChange && (
        <div
          role="group"
          aria-label={t('ui.density')}
          className="inline-flex overflow-hidden rounded-sm border-[0.5px] border-hairline-strong"
        >
          {densities.map((d) => (
            <button
              key={d}
              type="button"
              aria-pressed={density === d}
              onClick={() => onDensityChange(d)}
              className={cn(
                'px-sm py-2xs text-label uppercase tracking-widest transition-colors duration-almanac',
                'focus-visible:shadow-focus focus-visible:relative focus-visible:z-1',
                density === d
                  ? 'bg-accent-blue text-canvas-paper'
                  : 'bg-canvas-paper text-ink-mid hover:bg-canvas-bone',
              )}
            >
              {t(`ui.density_${d}`)}
            </button>
          ))}
        </div>
      )}

      {onHiddenColumnsChange && (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className={cn(
                'inline-flex items-center gap-2xs rounded-sm border-[0.5px] border-hairline-strong bg-canvas-paper',
                'px-sm py-2xs text-label uppercase tracking-widest text-ink-mid',
                'transition-colors duration-almanac hover:bg-canvas-bone focus-visible:shadow-focus',
              )}
            >
              {t('ui.columns')}
              <span aria-hidden>▾</span>
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={4}
              className="z-50 min-w-[200px] rounded-md border-[0.5px] border-hairline bg-canvas-paper p-2xs shadow-nav-scrolled"
            >
              <DropdownMenu.Label className="px-sm py-2xs text-label uppercase tracking-widest text-ink-mute">
                {t('ui.columnsHint')}
              </DropdownMenu.Label>
              {columns.map((c) => (
                <DropdownMenu.CheckboxItem
                  key={c.id}
                  checked={!hidden.has(c.id)}
                  onCheckedChange={() => toggleColumn(c.id)}
                  onSelect={(e) => e.preventDefault()}
                  className={cn(
                    'flex cursor-pointer select-none items-center gap-xs rounded-xs px-sm py-2xs text-small text-ink-graphite',
                    'outline-hidden data-highlighted:bg-canvas-bone',
                  )}
                >
                  <DropdownMenu.ItemIndicator className="w-[12px]">✓</DropdownMenu.ItemIndicator>
                  <span className={cn(hidden.has(c.id) && 'ms-[20px]')}>{c.label}</span>
                </DropdownMenu.CheckboxItem>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )}
    </div>
  );
}

/**
 * Watches a scroll frame and reports whether it is CURRENTLY overflowing.
 *
 * Measured rather than assumed: the same table overflows on a phone and does
 * not on a desktop, and it starts and stops overflowing while the console is
 * open — a column is hidden from the toolbar, the window is resized, a page of
 * rows with longer names arrives. Both the frame and its table are observed,
 * because only one of the two changes in each of those cases.
 */
function useScrollableFrame() {
  const ref = useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    // A pixel of slack: sub-pixel layout rounding otherwise reports an overflow
    // of 0.3px and puts a tab stop on a table that does not scroll.
    const measure = () =>
      setScrollable(el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, []);
  return [ref, scrollable] as const;
}
